import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { apiService } from "../../services/api";
import { dictionaries } from "../../utils/dictionary";
import { Tournament, Club } from "../../types";
import {
  PhaseButton,
  ModalOverlay,
  ModalCard,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalTitle,
  ModalCloseButton,
} from "../../styles/SharedStyles";
import { theme } from "../../styles/theme";

interface MainMenuProps {
  language: number;
  onLanguageChange: (language: number) => void;
  onTournamentSelect: (tournament: Tournament) => void;
  onClubSelect: (club: Club) => void;
  onLogout: () => void;
  userName: string;
  userAvatar: string;
}

const MenuBackground = styled.div`
  min-height: 100vh;
  background: radial-gradient(
      circle at top,
      rgba(124, 93, 255, 0.25),
      transparent 55%
    ),
    radial-gradient(
      circle at bottom right,
      rgba(32, 18, 72, 0.6),
      transparent 60%
    ),
    ${theme.colors.background.setup};
  color: ${theme.colors.text.primary};
  padding: ${theme.spacing.xl};
  contain: layout style;

  * {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.lg};
  }
`;

const MenuPage = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xl};
`;

const MenuHeader = styled.header`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
  padding: ${theme.spacing.xl};
  background: rgba(18, 12, 36, 0.94);
  border-radius: ${theme.borderRadius.xxl};
  border: 1px solid ${theme.colors.border.primary};
  box-shadow: ${theme.shadows.lg};

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.lg};
  }
`;

const MenuHeaderTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing.lg};

  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${theme.spacing.md};
  }
`;

const MenuHeaderTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Eyebrow = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: ${theme.colors.text.muted};
`;

const MainTitle = styled.h1`
  font-size: 2.3rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  margin: 0;
  color: ${theme.colors.text.primary};

  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 2rem;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1.8rem;
  }
`;

const Subtitle = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: 0.95rem;
  margin: 0;
`;

const MenuHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  flex-shrink: 0;

  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 100%;
  }
`;

const ProfileButton = styled.button.attrs({ type: "button" })`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.md};
  padding: 0.5rem 1rem;
  border-radius: ${theme.borderRadius.lg};
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid ${theme.colors.border.secondary};
  color: ${theme.colors.text.primary};
  cursor: pointer;
  transition: background 0.15s ease;
  flex-shrink: 0;
  contain: layout style paint;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 100%;
    justify-content: flex-start;
  }
`;

const ProfileAvatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px solid ${theme.colors.border.secondary};
`;

const ProfileIdentity = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.125rem;

  span {
    font-size: 0.75rem;
    color: ${theme.colors.text.muted};
  }

  strong {
    font-size: 0.9rem;
    font-weight: 600;
    color: ${theme.colors.text.primary};
  }
`;

const MenuContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xxl};
`;

const MenuSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
  contain: layout style;
`;

const MenuSectionHeader = styled.div`
  padding-left: 0.75rem;

  h2 {
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0 0 0.5rem;
    color: ${theme.colors.text.primary};
  }

  p {
    font-size: 0.95rem;
    color: ${theme.colors.text.secondary};
    margin: 0;
  }
`;

const MenuGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: ${theme.spacing.lg};
  contain: layout;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.md};
  }

  @media (min-width: 769px) and (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const MenuCard = styled.button.attrs({ type: "button" })`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
  padding: ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.xl};
  background: rgba(18, 12, 36, 0.94);
  border: 1px solid ${theme.colors.border.primary};
  cursor: pointer;
  text-align: left;
  contain: layout style paint;
  backface-visibility: hidden;

  @media (hover: hover) {
    transition: border-color 0.2s ease, background 0.2s ease;
    
    &:hover {
      background: rgba(22, 14, 42, 0.96);
      border-color: ${theme.colors.border.strong};
    }
  }

  &:active {
    background: rgba(18, 12, 36, 0.94);
  }
`;

const MenuCardTop = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing.md};
`;

const MenuCardFlag = styled.img`
  width: 48px;
  height: 48px;
  border-radius: ${theme.borderRadius.md};
  border: 2px solid ${theme.colors.border.secondary};
  object-fit: cover;
  pointer-events: none;
  user-select: none;
`;

const MenuCardInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const MenuCardTitle = styled.div`
  font-size: 1.1rem;
  font-weight: 700;
  color: ${theme.colors.text.primary};
`;

const MenuCardMeta = styled.div`
  font-size: 0.85rem;
  color: ${theme.colors.text.secondary};
`;

const MenuCardCTA = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  color: rgba(124, 93, 255, 0.9);
  transition: color 0.15s ease;

  ${MenuCard}:hover & {
    color: rgba(124, 93, 255, 1);
  }
`;

const MenuEmpty = styled.div`
  padding: ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.lg};
  background: rgba(255, 255, 255, 0.03);
  border: 1px dashed ${theme.colors.border.secondary};
  color: ${theme.colors.text.secondary};
  text-align: center;
  font-size: 0.95rem;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.lg};
  min-height: 60vh;
  color: ${theme.colors.text.secondary};

  p {
    font-size: 1.1rem;
    margin: 0;
  }
`;

const Spinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid rgba(124, 93, 255, 0.2);
  border-top-color: rgba(124, 93, 255, 0.8);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ProfileDialogContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.lg};
  padding: ${theme.spacing.lg} 0;

  img {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    border: 3px solid ${theme.colors.border.primary};
  }

  > div {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;

    .text-muted {
      font-size: 0.85rem;
      color: ${theme.colors.text.muted};
    }

    strong {
      font-size: 1.1rem;
      font-weight: 700;
      color: ${theme.colors.text.primary};
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  justify-content: flex-end;
`;

const LanguageSection = styled.div`
  margin-top: ${theme.spacing.lg};
  padding-top: ${theme.spacing.lg};
  border-top: 1px solid ${theme.colors.border.secondary};
`;

const LanguageLabel = styled.label`
  display: block;
  margin-bottom: ${theme.spacing.sm};
  font-weight: 600;
  font-size: 0.9rem;
  color: ${theme.colors.text.primary};
`;

const LanguageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: ${theme.spacing.sm};
  margin-top: ${theme.spacing.md};

  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const LanguageButton = styled.button.attrs({ type: 'button' })<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  background: ${({ $active }) =>
    $active
      ? 'linear-gradient(135deg, rgba(124, 93, 255, 0.45), rgba(70, 46, 150, 0.65))'
      : 'rgba(255, 255, 255, 0.05)'};
  border: 1px solid
    ${({ $active }) =>
      $active ? 'rgba(124, 93, 255, 0.65)' : theme.colors.border.secondary};
  color: ${theme.colors.text.primary};
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
  white-space: nowrap;
  contain: layout style paint;

  &:hover {
    background: ${({ $active }) =>
      $active
        ? 'linear-gradient(135deg, rgba(124, 93, 255, 0.55), rgba(70, 46, 150, 0.75))'
        : 'rgba(255, 255, 255, 0.12)'};
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.85rem;
    padding: 0.75rem 0.5rem;
    gap: 0.35rem;
  }
`;

const LANGUAGES = [
  { id: 0, name: 'English', flag: '🇬🇧' },
  { id: 1, name: 'Русский', flag: '🏳️' },
  { id: 2, name: 'Українська', flag: '🇺🇦' },
  { id: 3, name: 'ქართული', flag: '🇬🇪' },
  { id: 4, name: 'עברית', flag: '🇮🇱' },
];

const MainMenu: React.FC<MainMenuProps> = ({
  language,
  onLanguageChange,
  onTournamentSelect,
  onClubSelect,
  onLogout,
  userName,
  userAvatar,
}) => {
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [clubs, setClubs] = useState<Club[]>([]);
  const [loading, setLoading] = useState(true);
  const [showProfile, setShowProfile] = useState(false);
  const hasLoadedRef = React.useRef(false);

  const dict = dictionaries[language].ph;

  useEffect(() => {
    // Prevent double loading in React 18 StrictMode
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;
    
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [tournamentsResponse, clubsResponse] = await Promise.all([
        apiService.getTournaments(),
        apiService.loadClubs(),
      ]);

      if (tournamentsResponse.success) {
        setTournaments(tournamentsResponse.success);
      }

      if (clubsResponse.success) {
        setClubs(clubsResponse.success);
      }
    } catch (error) {
      console.error("Failed to load data:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadAllTournaments = async () => {
    setLoading(true);
    try {
      const tournamentsResponse = await apiService.getAllTournaments();

      if (tournamentsResponse.success) {
        setTournaments(tournamentsResponse.success);
      }
    } catch (error) {
      console.error("Failed to load all tournaments:", error);
    } finally {
      setLoading(false);
    }
  };

  const getFlagId = (tournamentId: number, regionId: number): number => {
    const tournamentFlags: Record<number, number> = {
      3379: 99,
      3406: 99,
      3477: 99,
      3483: 99,
      2894: 98,
      2883: 98,
    };
    return tournamentFlags[tournamentId] || regionId;
  };

  const handleProfile = () => {
    setShowProfile(!showProfile);
  };

  const handleLanguageChange = (newLanguage: number) => {
    onLanguageChange(newLanguage);
  };

  const trimmedHeader = dict.header?.trim?.() || dict.header || dict.mafia_app;
  const mainMenuLabel = dict.mmenu;
  const loadAllLabel = dict.loadall || dict.refresh;
  const loadingLabel = dict.loading;
  const selectClubLabel = dict.sclub || dict.select_club;
  const logoutLabel = dict.logout;
  const closeLabel = dict.close;
  const loggedInAsLabel = (dict.logedas).trim();

  if (loading) {
    return (
      <MenuBackground>
        <LoadingContainer>
          <Spinner role="status" aria-hidden="true" />
          <p>{loadingLabel}...</p>
        </LoadingContainer>
      </MenuBackground>
    );
  }

  return (
    <>
      <MenuBackground>
        <MenuPage>
          <MenuHeader>
            <MenuHeaderTop>
              <MenuHeaderTitle>
                <Eyebrow>{mainMenuLabel}</Eyebrow>
                <MainTitle>{trimmedHeader}</MainTitle>
                <Subtitle>{dict.main_menu_subtitle}</Subtitle>
              </MenuHeaderTitle>
              <MenuHeaderActions>
                <ProfileButton onClick={handleProfile}>
                  <ProfileAvatar
                    src={userAvatar}
                    alt={dict.player_avatar_alt.replace("{0}", userName)}
                  />
                  <ProfileIdentity>
                    <span>{loggedInAsLabel}</span>
                    <strong>{userName}</strong>
                  </ProfileIdentity>
                </ProfileButton>
              </MenuHeaderActions>
            </MenuHeaderTop>
          </MenuHeader>

          <MenuContent>
            <MenuSection>
              <MenuSectionHeader>
                <h2>{selectClubLabel}</h2>
                <p>{dict.club_section_description}</p>
              </MenuSectionHeader>
              {clubs.length > 0 ? (
                <MenuGrid>
                  {clubs.map((club) => (
                    <MenuCard key={club.id} onClick={() => onClubSelect(club)}>
                      <MenuCardTop>
                        <MenuCardFlag
                          src={club.logo}
                          alt={dict.club_logo_alt.replace("{0}", club.name)}
                          loading="lazy"
                          decoding="async"
                        />
                        <MenuCardInfo>
                          <MenuCardTitle>{club.name}</MenuCardTitle>
                          <MenuCardMeta>{dict.club_card_meta}</MenuCardMeta>
                        </MenuCardInfo>
                      </MenuCardTop>
                      <MenuCardCTA>
                        {dict.open_club}
                        <span aria-hidden="true">→</span>
                      </MenuCardCTA>
                    </MenuCard>
                  ))}
                </MenuGrid>
              ) : (
                <MenuEmpty>{dict.no_clubs_synced}</MenuEmpty>
              )}
            </MenuSection>

            <MenuSection>
              <MenuSectionHeader>
                <h2>{dict.tournaments_heading}</h2>
                <p>{dict.tournament_section_description}</p>
              </MenuSectionHeader>
              {tournaments.length > 0 ? (
                <>
                  <MenuGrid>
                    {tournaments.map((tournament) => (
                      <MenuCard
                        key={tournament.tournament_id}
                        onClick={() => onTournamentSelect(tournament)}
                      >
                        <MenuCardTop>
                          <MenuCardFlag
                            src={`img/region/${getFlagId(
                              tournament.tournament_id,
                              tournament.region_id
                            )}.png`}
                            alt={dict.tournament_region_flag}
                            loading="lazy"
                            decoding="async"
                          />
                          <MenuCardInfo>
                            <MenuCardTitle>
                              {tournament.tournament_name}
                            </MenuCardTitle>
                            <MenuCardMeta>{dict.tournament_card_meta}</MenuCardMeta>
                          </MenuCardInfo>
                        </MenuCardTop>
                        <MenuCardCTA>
                          {dict.view_seats}
                          <span aria-hidden="true">→</span>
                        </MenuCardCTA>
                      </MenuCard>
                    ))}
                  </MenuGrid>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      marginTop: "1rem",
                    }}
                  >
                    <PhaseButton
                      $variant="outline"
                      onClick={loadAllTournaments}
                      disabled={loading}
                    >
                      ⟳ {loadAllLabel}
                    </PhaseButton>
                  </div>
                </>
              ) : (
                <MenuEmpty>{dict.no_tournaments}</MenuEmpty>
              )}
            </MenuSection>
          </MenuContent>
        </MenuPage>
      </MenuBackground>

      {showProfile && (
        <ModalOverlay
          role="dialog"
          aria-modal="true"
          aria-label={dict.profile_aria_label}
        >
          <ModalCard>
            <ModalHeader>
              <ModalTitle>{dict.profile_title}</ModalTitle>
              <ModalCloseButton
                aria-label={closeLabel}
                onClick={() => setShowProfile(false)}
              >
                ×
              </ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              <ProfileDialogContent>
                <img
                  src={userAvatar}
                  alt={dict.player_avatar_alt.replace("{0}", userName)}
                />
                <div>
                  <div className="text-muted">{loggedInAsLabel}</div>
                  <strong>{userName}</strong>
                </div>
              </ProfileDialogContent>
              
              <LanguageSection>
                <LanguageLabel>{dict.app_language_label}</LanguageLabel>
                <LanguageGrid>
                  {LANGUAGES.map((lang) => (
                    <LanguageButton
                      key={lang.id}
                      $active={language === lang.id}
                      onClick={() => handleLanguageChange(lang.id)}
                    >
                      <span>{lang.flag}</span>
                      <span>{lang.name}</span>
                    </LanguageButton>
                  ))}
                </LanguageGrid>
              </LanguageSection>
            </ModalBody>
            <ModalFooter>
              <ButtonGroup>
                <PhaseButton $variant="danger" onClick={onLogout}>
                  {logoutLabel}
                </PhaseButton>
                <PhaseButton
                  $variant="secondary"
                  onClick={() => setShowProfile(false)}
                >
                  {closeLabel}
                </PhaseButton>
              </ButtonGroup>
            </ModalFooter>
          </ModalCard>
        </ModalOverlay>
      )}
    </>
  );
};

export default MainMenu;
